/**
 * @fileoverview SubmitNarrativeReviewDialog — last look at thin narratives
 * before a day, week or month of time goes for approval (MOR-1039).
 *
 * Lists the entries in the period whose narrative trips the ARITA prompt and
 * lets the practitioner go back and fix them or submit anyway. It is a nudge,
 * never a gate: "Submit anyway" always works.
 */

import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import type { FlaggedNarrative } from "./weekGrid";

/**
 * The firm's own narrative guidance (MOR-1039). Shown in the dialog's
 * description; when the firm has not entered any, the built-in prompt stands
 * in so the dialog never reads as empty.
 */
const DEFAULT_GUIDANCE =
  "ARITA expects details of the work performed, specific to the appointment, with more explanation for longer entries.";

export function SubmitNarrativeReviewDialog({
  open,
  flagged,
  periodLabel,
  submitting,
  fmtHours,
  guidanceText,
  onOpenChange,
  onSubmitAnyway,
}: {
  open: boolean;
  flagged: FlaggedNarrative[];
  /** "day" | "week" | "month" — completes "Submit {period} anyway". */
  periodLabel: string;
  submitting: boolean;
  fmtHours: (units: number) => string;
  /** The firm's guidance; blank falls back to the built-in prompt. */
  guidanceText?: string;
  onOpenChange: (open: boolean) => void;
  onSubmitAnyway: () => void;
}) {
  const count = flagged.length;
  const guidance =
    guidanceText && guidanceText.trim().length > 0
      ? guidanceText.trim()
      : DEFAULT_GUIDANCE;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {count === 1
              ? "1 narrative may need more detail"
              : `${count} narratives may need more detail`}
          </AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {guidance} You can go back and add detail, or submit as is.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ul className="max-h-72 space-y-2 overflow-y-auto">
          {flagged.map(({ entry, warning }) => (
            <li
              key={entry._id}
              className="rounded-md border border-border/60 px-3 py-2 text-sm"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs text-muted-foreground">
                <span>
                  {format(parseISO(entry.date), "EEE d MMM")} · {entry.jobName}
                </span>
                <span className="tabular-nums">{fmtHours(entry.units)}</span>
              </div>
              <p className="mt-1">{entry.narrative}</p>
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {warning.kind === "generic"
                  ? "Reads as generic."
                  : "Looks brief for the time recorded."}
              </p>
            </li>
          ))}
        </ul>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>
            Go back and edit
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={submitting}
            onClick={(e) => {
              // Keep the dialog up until the submit settles.
              e.preventDefault();
              onSubmitAnyway();
            }}
          >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            Submit {periodLabel} anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
